import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaSearch,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaCarAlt,
  FaCalendarAlt,
  FaChevronDown,
  FaChevronUp,
  FaChevronLeft,
  FaChevronRight,
  FaClipboardList,
  FaFilter,
  FaSortAmountDown,
  FaSortAmountUpAlt,
  FaTimes
} from "react-icons/fa";

import moment from "moment-timezone";

const CUSTOMERS_PER_PAGE = 10;

function Customers({ rentalsData, isLoading, refetch, setSelectedRental, setIsEditRentalModalOpen }) {  const [customers, setCustomers] = useState([]);
  const [adminEntries, setAdminEntries] = useState([]); // Separate state for admin entries
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showAdminEntries, setShowAdminEntries] = useState(false); // Toggle for admin entries view
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'rentalCount', direction: 'desc' });
  const [filterRentalCount, setFilterRentalCount] = useState(0);
  const [showFilters, setShowFilters] = useState(false);    // Process rentals to extract unique customers and their rental counts
  useEffect(() => {
    if (!rentalsData || !rentalsData.length) return;
    
    const customerMap = new Map();
    const adminMap = new Map();
    
    rentalsData.forEach(rental => {
      // Handle admin entries differently
      const isAdminEntry = rental.customer.email === "nleont@gmail.com" || rental.customer.email === "niotisan@gmail.com";
      
      // Use email as the primary key to group rentals by the same email
      const customerKey = rental.customer.email;
      
      if (isAdminEntry) {
        // Process admin entries
        if (!adminMap.has(customerKey)) {
          adminMap.set(customerKey, {
            firstName: rental.customer.firstName,
            lastName: rental.customer.lastName,
            email: rental.customer.email,
            phoneNumber: rental.customer.phoneNumber,
            age: rental.customer.age,
            isAdmin: true,
            nameVariants: new Set([`${rental.customer.firstName} ${rental.customer.lastName}`]),
            rentalCount: 1,
            totalSpent: parseFloat(rental.totalPrice || 0),
            lastRental: new Date(rental.pickupDateTime),
            rentals: [rental]
          });
        } else {
          const admin = adminMap.get(customerKey);
          admin.rentalCount += 1;
          admin.totalSpent += parseFloat(rental.totalPrice || 0);
          admin.nameVariants.add(`${rental.customer.firstName} ${rental.customer.lastName}`);
          
          const rentalDate = new Date(rental.pickupDateTime);
          if (rentalDate > admin.lastRental) {
            admin.lastRental = rentalDate;
          }
          admin.rentals.push(rental);
          adminMap.set(customerKey, admin);
        }
      } else {
        // Process regular customer entries
        if (!customerMap.has(customerKey)) {
          customerMap.set(customerKey, {
            firstName: rental.customer.firstName,
            lastName: rental.customer.lastName,
            email: rental.customer.email,
            phoneNumber: rental.customer.phoneNumber,
            age: rental.customer.age,
            isAdmin: false,
            nameVariants: new Set([`${rental.customer.firstName} ${rental.customer.lastName}`]),
            rentalCount: 1,
            totalSpent: parseFloat(rental.totalPrice || 0),
            lastRental: new Date(rental.pickupDateTime),
            rentals: [rental]
          });
        } else {
          const customer = customerMap.get(customerKey);
          customer.rentalCount += 1;
          customer.totalSpent += parseFloat(rental.totalPrice || 0);
          customer.nameVariants.add(`${rental.customer.firstName} ${rental.customer.lastName}`);
          
          const rentalDate = new Date(rental.pickupDateTime);
          if (rentalDate > customer.lastRental) {
            customer.lastRental = rentalDate;
          }
          customer.rentals.push(rental);
          customerMap.set(customerKey, customer);
        }
      }
    });
    
    // Convert Sets to Arrays for easier handling in rendering
    let customersList = Array.from(customerMap.values()).map(customer => {
      customer.nameVariants = Array.from(customer.nameVariants);
      return customer;
    });
    
    let adminList = Array.from(adminMap.values()).map(admin => {
      admin.nameVariants = Array.from(admin.nameVariants);
      return admin;
    });
    
    // Apply sorting based on sortConfig
    sortCustomers(customersList);
    sortCustomers(adminList); // Sort admin entries too
    
    setCustomers(customersList);
    setAdminEntries(adminList);
    setTotalPages(Math.ceil(customersList.length / CUSTOMERS_PER_PAGE));
  }, [rentalsData, sortConfig]);
    // Sort function
  const sortCustomers = (customersArray) => {
    customersArray.sort((a, b) => {
      if (sortConfig.key === 'name') {
        // Use the first name variant for sorting by name
        const nameA = a.nameVariants[0].toLowerCase();
        const nameB = b.nameVariants[0].toLowerCase();
        return sortConfig.direction === 'asc' 
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      }
      
      if (sortConfig.key === 'lastRental') {
        return sortConfig.direction === 'asc'
          ? a.lastRental - b.lastRental
          : b.lastRental - a.lastRental;
      }
      
      if (sortConfig.key === 'totalSpent') {
        return sortConfig.direction === 'asc'
          ? a.totalSpent - b.totalSpent
          : b.totalSpent - a.totalSpent;
      }
      
      return sortConfig.direction === 'asc'
        ? a[sortConfig.key] - b[sortConfig.key]
        : b[sortConfig.key] - a[sortConfig.key];
    });
  };
  
  // Request sort
  const requestSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };  // Filter customers based on search query and rental count
  const filteredCustomers = customers.filter(customer => {
    // Filter by search query
    const matchesSearch = searchQuery === "" || 
      customer.nameVariants.some(name => name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      customer.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.phoneNumber.includes(searchQuery);
      
    // Filter by rental count
    const matchesRentalCount = filterRentalCount === 0 || customer.rentalCount >= filterRentalCount;
    
    return matchesSearch && matchesRentalCount;
  });
  
  // Filter admin entries similarly
  const filteredAdminEntries = adminEntries.filter(admin => {
    const matchesSearch = searchQuery === "" || 
      admin.nameVariants.some(name => name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      admin.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      admin.phoneNumber.includes(searchQuery);
      
    const matchesRentalCount = filterRentalCount === 0 || admin.rentalCount >= filterRentalCount;
    
    return matchesSearch && matchesRentalCount;
  });
  
  // Get paginated customers based on current view
  const currentDataset = showAdminEntries ? filteredAdminEntries : filteredCustomers;
  
  const paginatedCustomers = currentDataset.slice(
    (currentPage - 1) * CUSTOMERS_PER_PAGE,
    currentPage * CUSTOMERS_PER_PAGE
  );
  
  // Update total pages when switching between customers and admin entries
  useEffect(() => {
    setCurrentPage(1); // Reset to first page when switching views
    setTotalPages(Math.ceil((showAdminEntries ? filteredAdminEntries.length : filteredCustomers.length) / CUSTOMERS_PER_PAGE));
  }, [showAdminEntries, filteredAdminEntries.length, filteredCustomers.length]);

  // Handle viewing rental details
  const handleViewRental = (rental) => {
    setSelectedRental(rental);
    setIsEditRentalModalOpen(true);
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    return moment(dateString).format("DD/MM/YYYY");
  };
  
  // Clear all filters
  const clearFilters = () => {
    setSearchQuery("");
    setFilterRentalCount(0);
    setSortConfig({ key: 'rentalCount', direction: 'desc' });
  };  return (
    <div className="space-y-6">
      {/* Loading indicator */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading customers...</span>
        </div>
      )}

      {/* Search and Filter Section */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row justify-between items-center mb-4">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-bold text-gray-800 mb-4 md:mb-0">Customer Management</h2>
            
            {/* Toggle between regular customers and admin entries */}
            <div className="flex items-center gap-3 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setShowAdminEntries(false)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  !showAdminEntries 
                    ? 'bg-white text-blue-700 shadow-sm' 
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                Customers ({filteredCustomers.length})
              </button>
              <button
                onClick={() => setShowAdminEntries(true)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  showAdminEntries 
                    ? 'bg-white text-purple-700 shadow-sm' 
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                Admin Entries ({filteredAdminEntries.length})
              </button>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setShowFilters(!showFilters)} 
              className="flex items-center gap-1 px-3 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200"
            >
              <FaFilter />
              <span>Filters</span>
              {showFilters ? <FaChevronUp className="ml-1" /> : <FaChevronDown className="ml-1" />}
            </button>
            
            {(searchQuery !== "" || filterRentalCount > 0 || sortConfig.key !== 'rentalCount' || sortConfig.direction !== 'desc') && (
              <button 
                onClick={clearFilters} 
                className="flex items-center gap-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                <FaTimes />
                <span>Clear Filters</span>
              </button>
            )}
          </div>
        </div>
        
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3 mb-2">
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Name, email or phone..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 pl-10"
                    />
                    <FaSearch className="absolute left-3 top-3 text-gray-400" />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min. Rental Count</label>
                  <select
                    value={filterRentalCount}
                    onChange={(e) => setFilterRentalCount(parseInt(e.target.value))}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2"
                  >
                    <option value="0">All Customers</option>
                    <option value="2">2+ Rentals</option>
                    <option value="3">3+ Rentals</option>
                    <option value="5">5+ Rentals</option>
                    <option value="10">10+ Rentals</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
                  <div className="flex items-center gap-2">
                    <select
                      value={sortConfig.key}
                      onChange={(e) => setSortConfig({...sortConfig, key: e.target.value})}
                      className="flex-grow border border-gray-300 rounded-lg px-4 py-2"
                    >
                      <option value="name">Name</option>
                      <option value="rentalCount">Rental Count</option>
                      <option value="totalSpent">Total Spent</option>
                      <option value="lastRental">Last Rental Date</option>
                    </select>
                    <button
                      onClick={() => setSortConfig({
                        ...sortConfig, 
                        direction: sortConfig.direction === 'asc' ? 'desc' : 'asc'
                      })}
                      className="p-2 border border-gray-300 rounded-lg bg-gray-50 hover:bg-gray-100"
                    >
                      {sortConfig.direction === 'asc' 
                        ? <FaSortAmountUpAlt className="text-gray-600" /> 
                        : <FaSortAmountDown className="text-gray-600" />}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>        
        <div className="mt-3 text-sm text-gray-500">
          {showAdminEntries ? (
            <>
              Showing {filteredAdminEntries.length} admin entries
              {searchQuery && <span> (filtered from {adminEntries.length})</span>}
            </>
          ) : (
            <>
              Showing {filteredCustomers.length} customers 
              {searchQuery && <span> (filtered from {customers.length})</span>}
            </>
          )}
        </div>
      </div>
      
      {/* Customers Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rentals
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Spent
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Rental
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedCustomers.map((customer, index) => {
                const customerKey = customer.isAdmin 
                  ? `${customer.email}-admin-${customer.phoneNumber}`
                  : customer.email;
                  
                return (
                <React.Fragment key={customerKey}>
                  <tr className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${
                          customer.isAdmin ? 'bg-purple-100' : 'bg-blue-100'
                        }`}>
                          <FaUser className={`h-5 w-5 ${customer.isAdmin ? 'text-purple-600' : 'text-blue-600'}`} />
                        </div>
                        <div className="ml-4">
                          {customer.isAdmin ? (
                            <div className="text-sm font-medium text-gray-900 flex items-center">
                              Administrator
                              <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-800 text-xs rounded-full">Admin</span>
                            </div>
                          ) : customer.nameVariants.length === 1 ? (
                            <div className="text-sm font-medium text-gray-900">{customer.nameVariants[0]}</div>
                          ) : (
                            <div className="text-sm font-medium text-gray-900">
                              {customer.nameVariants.length <= 2 
                                ? customer.nameVariants.join(", ")
                                : `${customer.nameVariants[0]}, ${customer.nameVariants[1]}, +${customer.nameVariants.length - 2} more`
                              }
                              <div className="text-xs text-gray-500 mt-1">
                                Multiple names used with this email
                              </div>
                            </div>
                          )}
                          {customer.age && !customer.isAdmin && (
                            <div className="text-sm text-gray-500">Age: {customer.age}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 flex items-center">
                        <FaEnvelope className="mr-2 text-gray-400" />
                        {customer.email}
                      </div>
                      <div className="text-sm text-gray-500 flex items-center mt-1">
                        <FaPhone className="mr-2 text-gray-400" />
                        {customer.phoneNumber}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {customer.rentalCount}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      €{customer.totalSpent.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(customer.lastRental)}
                    </td>                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => setExpandedCustomer(expandedCustomer === customerKey ? null : customerKey)}
                        className="text-blue-600 hover:text-blue-900 flex items-center"
                      >
                        {expandedCustomer === customerKey ? (
                          <>
                            <FaChevronUp className="mr-1" />
                            Hide History
                          </>
                        ) : (
                          <>
                            <FaChevronDown className="mr-1" />
                            View History
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                  
                  {/* Expanded Rental History */}
                  {expandedCustomer === customerKey && (
                    <tr>
                      <td colSpan="6" className="px-6 py-4">
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="bg-gray-50 p-4 rounded-lg border border-gray-200"
                        >
                          <h4 className="text-md font-medium text-gray-700 mb-3 flex items-center">
                            <FaClipboardList className="mr-2 text-blue-600" />
                            Rental History
                          </h4>
                          
                          <div className="mt-3 grid grid-cols-1 gap-3">
                            {customer.rentals.sort((a, b) => new Date(b.pickupDateTime) - new Date(a.pickupDateTime)).map((rental) => (
                              <div key={rental._id} className="bg-white p-3 rounded-lg border border-gray-200 flex flex-col sm:flex-row justify-between">
                                <div className="flex-grow">
                                  <div className="flex items-center">
                                    <FaCarAlt className="text-blue-600 mr-2" />
                                    <span className="font-medium">{rental.carName}</span>
                                  </div>
                                  <div className="flex items-center mt-2 text-sm text-gray-600">
                                    <FaCalendarAlt className="text-green-600 mr-2" />
                                    <span>
                                      {formatDate(rental.pickupDateTime)} to {formatDate(rental.returnDateTime)}
                                    </span>
                                  </div>
                                  <div className="mt-2 text-sm flex flex-wrap gap-2">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      rental.status === "completed" 
                                        ? "bg-green-100 text-green-800" 
                                        : rental.status === "cancelled" 
                                        ? "bg-red-100 text-red-800" 
                                        : rental.status === "confirmed" 
                                        ? "bg-blue-100 text-blue-800" 
                                        : "bg-yellow-100 text-yellow-800"
                                    }`}>
                                      {rental.status.charAt(0).toUpperCase() + rental.status.slice(1)}
                                    </span>
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      rental.isPaid ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                                    }`}>
                                      {rental.isPaid ? "Paid" : "Unpaid"}
                                    </span>
                                  </div>
                                </div>
                                <div className="mt-3 sm:mt-0 flex items-center">
                                  <span className="font-medium text-gray-900 mr-4">€{rental.totalPrice}</span>
                                  <button
                                    onClick={() => handleViewRental(rental)}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-sm"
                                  >
                                    View Details
                                  </button>
                                </div>                              </div>
                            ))}
                          </div>
                        </motion.div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
              })}
            </tbody>
          </table>
        </div>
          {paginatedCustomers.length === 0 && (
          <div className="text-center py-12">
            <FaSearch className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {showAdminEntries ? "No admin entries found" : "No customers found"}
            </h3>
            <p className="text-gray-500">
              Try adjusting your search criteria
            </p>
          </div>
        )}
      </div>
      
      {/* Pagination Controls */}
      {paginatedCustomers.length > 0 && (
        <div className="flex justify-between items-center mt-6">
          <div className="text-sm text-gray-500">
            Showing {(currentPage - 1) * CUSTOMERS_PER_PAGE + 1} to {Math.min(currentPage * CUSTOMERS_PER_PAGE, currentDataset.length)} of {currentDataset.length} {showAdminEntries ? "admin entries" : "customers"}
          </div>
          
          <div className="flex items-center gap-4">
            <button
              onClick={() => {
                setCurrentPage(Math.max(currentPage - 1, 1));
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              disabled={currentPage === 1}
              className="px-4 py-2 rounded-md text-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaChevronLeft className="inline mr-1" /> Previous
            </button>
            <div className="flex items-center gap-2">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNumber =
                  currentPage <= 3
                    ? i + 1
                    : currentPage >= totalPages - 2
                    ? totalPages - 4 + i
                    : currentPage - 2 + i;

                if (pageNumber <= 0 || pageNumber > totalPages)
                  return null;

                return (
                  <button
                    key={pageNumber}
                    onClick={() => setCurrentPage(pageNumber)}
                    className={`w-8 h-8 flex items-center justify-center rounded-md ${
                      currentPage === pageNumber
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              })}

              {totalPages > 5 && currentPage < totalPages - 2 && (
                <>
                  {currentPage < totalPages - 3 && (
                    <span className="text-gray-500">...</span>
                  )}
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    className={`w-8 h-8 flex items-center justify-center rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200`}
                  >
                    {totalPages}
                  </button>
                </>
              )}
            </div>
            <button
              onClick={() => {
                setCurrentPage(Math.min(currentPage + 1, totalPages));
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              disabled={currentPage === totalPages}
              className="px-4 py-2 rounded-md text-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next <FaChevronRight className="inline ml-1" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Customers;
